import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import '../styles/ReportDetail.css';

function ReportDetail() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allReports, setAllReports] = useState([]);
  const { id } = useParams();
  const { token, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReport();
    fetchAllReports();
  }, [id]);

  const fetchReport = async () => {
    try {
      const data = await api.getReport(token, id);
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReports = async () => {
    try {
      const reports = await api.getReports(token);
      setAllReports(reports);
    } catch (err) {
      console.error('Error fetching all reports:', err);
    }
  };

  const getCurrentReportIndex = () => {
    return allReports.findIndex(r => r.id === parseInt(id));
  };

  const goToPreviousReport = () => {
    const currentIndex = getCurrentReportIndex();
    if (currentIndex > 0) {
      navigate(`/reports/${allReports[currentIndex - 1].id}`);
    }
  };

  const goToNextReport = () => {
    const currentIndex = getCurrentReportIndex();
    if (currentIndex < allReports.length - 1) {
      navigate(`/reports/${allReports[currentIndex + 1].id}`);
    }
  };

  const hasPrevious = () => {
    return getCurrentReportIndex() > 0;
  };

  const hasNext = () => {
    const currentIndex = getCurrentReportIndex();
    return currentIndex >= 0 && currentIndex < allReports.length - 1;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  const canEditReport = () => {
    // Admin can edit all reports, users can only edit their own
    return report && (isAdmin || report.user_id === user?.id);
  };

  if (loading) {
    return <div className="loading">Laster rapport...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/reports')} className="btn-primary">
          Tilbake til rapporter
        </button>
      </div>
    );
  }

  if (!report) {
    return <div>Rapport ikke funnet</div>;
  }

  return (
    <div className="report-detail-container">
      <div className="report-header">
        <div className="report-nav-buttons">
          <button
            onClick={goToPreviousReport}
            className="btn-nav"
            disabled={!hasPrevious()}
            title="Forrige rapport"
          >
            ← Forrige
          </button>
          <button
            onClick={goToNextReport}
            className="btn-nav"
            disabled={!hasNext()}
            title="Neste rapport"
          >
            Neste →
          </button>
        </div>
        {canEditReport() && (
          <button onClick={() => navigate(`/reports/${id}/edit`)} className="btn-primary">
            Rediger
          </button>
        )}
      </div>

      <div className="report-card">
        <div className="report-info-grid">
          <div className="info-item">
            <label>Skifttid:</label>
            <span>
              {formatDate(report.shift_date)} {formatTime(report.shift_start_time)} - {formatTime(report.shift_end_time)}
            </span>
          </div>
          <div className="info-item">
            <label>Bruker:</label>
            <span>{report.full_name}</span>
          </div>
        </div>

        {report.free_text && (
          <div className="free-text-section">
            <h3>Fritekst rapport ("Boka")</h3>
            <div className="free-text-content">{report.free_text}</div>
          </div>
        )}

        {report.checklist_responses && report.checklist_responses.length > 0 && (
          <div className="checklist-section">
            <div className="checklist-items">
              {report.checklist_responses.map((response) => (
                <div key={response.id} className="checklist-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={response.is_checked === 1}
                      disabled
                    />
                    <span>{response.item_text}</span>
                  </label>
                </div>
              ))}
            </div>
            {(report.checklist_comment || report.checklist_responses.some(r => r.is_checked === 0)) && (
              <div className="checklist-comment">
                <h4>Kommentar:</h4>
                <div className="comment-content">
                  {report.checklist_comment || '(Ingen kommentar lagt til)'}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="report-meta">
          <small>Opprettet: {formatDateTime(report.created_at)}</small>
          {report.updated_at && report.updated_at !== report.created_at && (
            <small> | Sist endret: {formatDateTime(report.updated_at)}</small>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportDetail;
